import clsx from "clsx";
import React, { type FC } from "react";

import { PublicationActionChip } from "./PublicationActionChip";

import { ChangeAction, type PageChangeSummary } from "api/api-types-publishing";
import { TIMELINE_VIEW_QUERY_PARAM } from "components/constants";
import { InteractionModeIcon } from "components/materials/topic/activity/detail/InteractionModeIcon";
import { InteractionMode } from "components/server-types";
import { t } from "i18n/i18n";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useNavigate } from "utils/routing";
import { pageUrl } from "utils/urls";

interface PageListProps {
  pages: PageChangeSummary[];
  sectionId: string;
  skipFirstDivider?: boolean;
  showTrailingDivider?: boolean;
  showStatusChip?: boolean;
  showViewPageHistory?: boolean;
}

export const PageList: FC<PageListProps> = ({
  pages,
  sectionId,
  skipFirstDivider = true,
  showTrailingDivider = false,
  showStatusChip = true,
  showViewPageHistory = true,
}) => {
  const navigate = useNavigate();
  if (pages.length === 0) return null;

  return (
    <div className="flex flex-col">
      {pages.map((page, index) => {
        const isDeleted = page.action === ChangeAction.delete;

        const content = (
          <>
            <IconText
              className={clsx(
                "min-w-0 flex-1 text-base font-medium",
                isDeleted ? "text-black-tint-55" : "text-black-tint-20",
              )}
              iconEnd={showStatusChip ? <PublicationActionChip action={page.action} /> : undefined}
              iconStart={
                page.interaction_mode !== InteractionMode.STATIC ? (
                  <InteractionModeIcon
                    interactionMode={page.interaction_mode}
                    variant={isDeleted ? "disabled" : "default"}
                  />
                ) : undefined
              }
              text={<span className={isDeleted ? "line-through" : ""}>{page.title}</span>}
            />

            {showViewPageHistory && page.action === ChangeAction.update && (
              <SquareButton
                className="invisible group-hover:visible"
                kind="tertiary"
                size="s"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(
                    pageUrl(sectionId, page.id, {
                      additionalParams: `${TIMELINE_VIEW_QUERY_PARAM}=1`,
                    }),
                  );
                }}
              >
                {t("publishing.view_page_history")}
              </SquareButton>
            )}
          </>
        );

        return (
          <div className="flex flex-col" key={page.id}>
            {!(skipFirstDivider && index === 0) && <HorizontalDivider />}
            {isDeleted ? (
              <div className="flex items-center justify-between px-2 py-2.5">{content}</div>
            ) : (
              <ListItem
                className="group !my-0 flex !h-auto items-center justify-between !rounded-none !border-none !px-2 !py-2.5"
                kind="secondary"
                to={pageUrl(sectionId, page.id)}
              >
                {content}
              </ListItem>
            )}
            {showTrailingDivider && index === pages.length - 1 && <HorizontalDivider />}
          </div>
        );
      })}
    </div>
  );
};
